
import { API_URL } from '../constants';

/**
 * Utilitário para comprimir imagens em Base64 no lado do cliente.
 * Redimensiona e converte para JPEG com qualidade controlada.
 */
export const compressBase64 = async (base64: string, maxWidth = 1000, quality = 0.5): Promise<string> => {
    // Se não for uma string base64 válida ou for um link externo, retorna como está
    if (!base64.startsWith('data:image')) return base64;

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = base64;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Mantém a proporção redimensionando para o limite máximo
            if (width > maxWidth) {
                height = (maxWidth / width) * height;
                width = maxWidth;
            }

            // Arredonda para evitar problemas de renderização com floats
            width = Math.floor(width);
            height = Math.floor(height);

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(base64); // Fallback
                return;
            }

            // Pinta fundo branco (importante para conversão PNG -> JPEG)
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            
            ctx.drawImage(img, 0, 0, width, height);

            // Exporta como JPEG (muito mais leve que PNG)
            const compressed = canvas.toDataURL('image/jpeg', quality);
            resolve(compressed);
        };
        img.onerror = (err) => {
            console.error("Erro ao comprimir imagem:", err);
            resolve(base64); // Em caso de erro, retorna a original
        };
    });
};

/**
 * Torna um nome de arquivo amigável (slugify).
 */
export const sanitizeFilename = (name: string): string => {
    // Remove a extensão para tratar o nome separadamente
    const lastDotIndex = name.lastIndexOf('.');
    let baseName = name;
    let extension = 'jpg';

    if (lastDotIndex !== -1) {
        baseName = name.substring(0, lastDotIndex);
        extension = name.substring(lastDotIndex + 1).toLowerCase();
    }

    const sanitizedBase = baseName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove acentos
        .replace(/[^a-z0-9]+/g, '-')    // Remove caracteres especiais
        .replace(/-+/g, '-')             // Remove hifens duplos
        .replace(/(^-|-$)/g, '');        // Remove hifens no início ou fim

    // Adiciona timestamp para garantir unicidade
    return `${sanitizedBase}-${Date.now()}.${extension}`;
};

/**
 * Converte Base64 para Blob e faz upload para o servidor via Multipart Form Data.
 * Retorna a URL da imagem salva no servidor.
 */
export const uploadImageToServer = async (base64Image: string, filename = 'image.jpg'): Promise<string> => {
    if (!base64Image) {
        return '';
    }

    // Se for uma URL externa (não local, ex de Pollinations ou OpenAI), faz upload enviando a URL
    const isExternalUrl = base64Image.startsWith('http') && 
                          !base64Image.includes(window.location.host) && 
                          !base64Image.startsWith('/') &&
                          !base64Image.includes('uploads/');

    try {
        if (isExternalUrl) {
            console.log('[uploadImageToServer] Uploading remote URL via server proxy:', base64Image);
            
            // Cria form-data com o campo JSON/POST apropriado ou como POST normal
            const formData = new FormData();
            formData.append('url', base64Image);
            formData.append('filename', filename);

            const response = await fetch(`${API_URL}?action=upload_file`, {
                method: 'POST',
                headers: {
                    'X-Admin-Token': localStorage.getItem('admin_token') || ''
                },
                body: formData
            });

            const textResponse = await response.text();
            let result;
            try {
                result = JSON.parse(textResponse);
            } catch (e) {
                console.error('[uploadImageToServer] Failed to parse remote upload JSON response, returning original url:', e);
                return base64Image;
            }

            if (response.ok && result.success && result.url) {
                const baseUrl = API_URL.replace('/api/index.php', '');
                const finalUrl = result.url.startsWith('/') || result.url.startsWith('http') 
                    ? result.url 
                    : `${baseUrl}/${result.url}`;
                if (finalUrl.startsWith('http')) return finalUrl;
                const cleanUrl = finalUrl.replace(/\/+/g, '/');
                return cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
            }

            console.warn('[uploadImageToServer] Server failed to download remote image, returning raw URL', result);
            return base64Image; 
        }

        // 1. Converter Base64 para Blob
        const fetchResponse = await fetch(base64Image);
        const blob = await fetchResponse.blob();

        // 2. Criar FormData
        const formData = new FormData();
        formData.append('file', blob, sanitizeFilename(filename));

        // 3. Enviar para API
        console.log('UPLOADING TO:', `${API_URL}?action=upload_file`);
        const response = await fetch(`${API_URL}?action=upload_file`, {
            method: 'POST',
            headers: {
                'X-Admin-Token': localStorage.getItem('admin_token') || ''
            },
            body: formData
        });

        console.log('UPLOAD RESPONSE STATUS:', response.status);
        const textResponse = await response.text();
        console.log('UPLOAD RESPONSE TEXT:', textResponse.substring(0, 200));
        
        let result;
        try {
            result = JSON.parse(textResponse);
        } catch (e) {
            console.error('FAILED TO PARSE JSON:', e);
            throw new Error(`Invalid JSON response: ${textResponse.substring(0, 50)}`);
        }

        if (response.ok && result.success && result.url) {
            // Garante que a URL seja absoluta ou raiz-relativa para evitar imagens quebradas em subpáginas
            const baseUrl = API_URL.replace('/api/index.php', '');
            const finalUrl = result.url.startsWith('/') || result.url.startsWith('http') 
                ? result.url 
                : `${baseUrl}/${result.url}`;
            
            // Se for absoluta, retorna direto sem mexer em slashes (para não quebrar https://)
            if (finalUrl.startsWith('http')) return finalUrl;
            
            // Se for relativa, limpa slashes duplos e garante o / inicial
            const cleanUrl = finalUrl.replace(/\/+/g, '/');
            return cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
        } else {
            console.error("Erro no upload:", result);
            // Em caso de falha no upload, retorna o base64 original para tentar salvar direto (fallback arriscado)
            // Ou lança erro
            throw new Error(result.error || "Falha no upload da imagem");
        }
    } catch (error) {
        console.error("Erro crítico no upload:", error);
        throw error;
    }
};
